import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EnvironmentParams, LoggingSettings, SplunkSettings } from '@shared/models/environment-params';
import { akitaDevtools, enableAkitaProdMode } from '@datorama/akita';
import { WEBCOMPONENT_ROOT_ELEMENT_GETTER, WebComponentRootElementGetter, STATIC_FILES_ENDPOINT } from '@shared/tokens';
import { type AppComponentInputs } from 'src/app/app.component';

@Injectable({
	providedIn: 'root'
})
export class AppSettingsService {
	private appSettings: EnvironmentParams;

	constructor(
		private http: HttpClient,
		@Inject(WEBCOMPONENT_ROOT_ELEMENT_GETTER) private readonly webComponentRootElementGetter: WebComponentRootElementGetter,
		@Optional() @Inject(STATIC_FILES_ENDPOINT) private readonly staticFilesEndpoint: string | null
	) {}

	loadAppSettings(): Promise<void> {
		const baseUrl =
			(this.webComponentRootElementGetter() as HTMLElement & AppComponentInputs)?.staticFilesEndpoint ||
			this.staticFilesEndpoint ||
			'';

		return this.http
			.get(baseUrl + '/assets/config/app.settings.json')
			.toPromise()
			.then((data: EnvironmentParams) => {
				this.appSettings = data;
				this.configureAkita(data.isProduction === true);
			});
	}

	get environmentParams(): EnvironmentParams | null {
		return this.appSettings ?? null;
	}

	get loggingSetting(): LoggingSettings | null {
		return this.appSettings?.logging ?? null;
	}

	get retryIntervals(): number[] {
		return this.appSettings?.retryIntervals ?? [];
	}

	get splunkSettings(): SplunkSettings {
		return this.appSettings?.signalFx;
	}

	get isProduction(): boolean {
		const isProductionType = typeof this.appSettings?.isProduction;

		return (
			(isProductionType === 'string' && this.appSettings.isProduction === 'true') ||
			(isProductionType === 'boolean' && this.appSettings.isProduction === true)
		);
	}

	configureAkita(isProduction: boolean) {
		if (isProduction) {
			enableAkitaProdMode();

			return;
		}

		const akitaDevToolsOptions = {
			logTrace: false,
			shallow: false,
			name: 'rx-app'
		};

		akitaDevtools(akitaDevToolsOptions);
	}

	isValidConfig(key: keyof EnvironmentParams): boolean {
		const value = this.appSettings?.[key];

		if (value == null || String(value) === '' || String(value).toLowerCase().includes('replaceme')) {
			return false;
		}

		return true;
	}
}
